import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { upload } from '@vercel/blob/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSpinner, faArrowUp, faArrowDown, faPen, faBold, faItalic } from '@fortawesome/free-solid-svg-icons';
import MyCarousel from '../../components/Carousel/Carousel';
import { applyFormatting } from '../../utils/formatting';
import { DEFAULT_CONTACT } from '../../components/ModaleContact/ModaleContact';
import './AdminPanel.scss';

const CAROUSEL_MAX_WIDTH = 1600;
const CAROUSEL_QUALITY = 0.82;
const BANNIERE_MAX_WIDTH = 1600;
const BANNIERE_QUALITY = 0.85;
// Doit rester identique au repli utilisé dans Presentation.js pour que
// l'adminpanel montre bien ce qui est actuellement en ligne.
const DEFAULT_BANNIERE = { type: 'video', url: '/assets/Home/video.mp4' };

function resizeImageToWebp(file, maxWidth = CAROUSEL_MAX_WIDTH, quality = CAROUSEL_QUALITY) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
        reader.onload = (e) => {
            const img = new Image();
            img.onerror = () => reject(new Error('Image invalide.'));
            img.onload = () => {
                const scale = Math.min(1, maxWidth / img.width);
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(
                    (blob) => (blob ? resolve(blob) : reject(new Error('Conversion image impossible.'))),
                    'image/webp',
                    quality
                );
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}

// Entoure la sélection courante du textarea (ou insère un texte de repli si
// rien n'est sélectionné) avec `before`/`after`, puis remet le focus dessus.
function wrapSelection(textarea, value, setValue, before, after) {
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end) || 'texte';
    const newValue = value.slice(0, start) + before + selected + after + value.slice(end);
    setValue(newValue);
    requestAnimationFrame(() => {
        textarea.focus();
        const selStart = start + before.length;
        textarea.setSelectionRange(selStart, selStart + selected.length);
    });
}

function AdminPanel() {
    const navigate = useNavigate();
    const [authChecked, setAuthChecked] = useState(false);

    const [horaire, setHoraire] = useState('');
    const [horaireStatus, setHoraireStatus] = useState('');
    const horaireRef = useRef(null);

    const [modale, setModale] = useState({ active: true, titre: '', message: '' });
    const [modaleStatus, setModaleStatus] = useState('');

    const [banniere, setBanniere] = useState(DEFAULT_BANNIERE);
    const [banniereStatus, setBanniereStatus] = useState('');
    const [isBanniereUploading, setIsBanniereUploading] = useState(false);

    const [photos, setPhotos] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isSavingCarousel, setIsSavingCarousel] = useState(false);
    const [isCarouselDirty, setIsCarouselDirty] = useState(false);
    const [carouselStatus, setCarouselStatus] = useState('');
    const originalCarouselRef = useRef([]);

    const [contact, setContact] = useState(DEFAULT_CONTACT);
    const [contactStatus, setContactStatus] = useState('');

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    // --- Vérification de session ---
    useEffect(() => {
        let cancelled = false;

        fetch('/api/auth/me', { credentials: 'include' })
            .then((r) => r.json())
            .then((data) => {
                if (cancelled) return;
                if (!data.authenticated) {
                    navigate('/');
                    return;
                }
                setAuthChecked(true);
            })
            .catch(() => { if (!cancelled) navigate('/'); });

        return () => { cancelled = true; };
    }, [navigate]);

    const loadContent = useCallback(() => {
        fetch('/api/horaire').then((r) => r.json()).then((d) => setHoraire(d.horaire || '')).catch(() => {});
        fetch('/api/modale').then((r) => r.json()).then((d) => setModale({
            active: d.active !== false,
            titre: d.titre || '',
            message: d.message || '',
        })).catch(() => {});
        fetch('/api/banniere').then((r) => r.json()).then((d) => {
            if (d && d.url) setBanniere({ type: d.type || 'video', url: d.url });
        }).catch(() => {});
        fetch('/api/carousel').then((r) => r.json()).then((d) => {
            const loaded = (d.photos || []).map((p) => ({ key: `existing-${p.id}`, id: p.id, url: p.url, file: null }));
            setPhotos(loaded);
            originalCarouselRef.current = loaded.map((p) => ({ id: p.id, url: p.url }));
            setIsCarouselDirty(false);
        }).catch(() => {});
        fetch('/api/contact').then((r) => r.json()).then((d) => {
            if (d && d.telephone) setContact(d);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (authChecked) loadContent();
    }, [authChecked, loadContent]);

    if (!authChecked) {
        return (
            <div className="adminPanel adminPanel--loading">
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    // --- Horaire ---
    const saveHoraire = async (e) => {
        e.preventDefault();
        setHoraireStatus('Enregistrement…');
        try {
            const res = await fetch('/api/horaire', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ horaire }),
            });
            const data = await res.json();
            setHoraireStatus(res.ok ? (data.message || 'Enregistré !') : (data.error || 'Erreur'));
        } catch {
            setHoraireStatus('Erreur réseau.');
        }
    };

    // --- Modale / annonce ---
    const saveModale = async (e) => {
        e.preventDefault();
        setModaleStatus('Enregistrement…');
        try {
            const res = await fetch('/api/modale', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(modale),
            });
            const data = await res.json();
            setModaleStatus(res.ok ? (data.message || 'Enregistré !') : (data.error || 'Erreur'));
        } catch {
            setModaleStatus('Erreur réseau.');
        }
    };

    // --- Contact ---
    const saveContact = async (e) => {
        e.preventDefault();
        setContactStatus('Enregistrement…');
        try {
            const res = await fetch('/api/contact', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(contact),
            });
            const data = await res.json();
            setContactStatus(res.ok ? (data.message || 'Enregistré !') : (data.error || 'Erreur'));
        } catch {
            setContactStatus('Erreur réseau.');
        }
    };

    // --- Bannière ---
    const handleBanniereUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        if (!isVideo && !isImage) {
            setBanniereStatus('Format non supporté (image ou vidéo uniquement).');
            return;
        }

        setIsBanniereUploading(true);
        setBanniereStatus(isVideo ? 'Envoi de la vidéo…' : 'Traitement de la photo…');
        try {
            let body = file;
            let filename;
            if (isImage) {
                body = await resizeImageToWebp(file, BANNIERE_MAX_WIDTH, BANNIERE_QUALITY);
                filename = `banniere/${Date.now()}.webp`;
            } else {
                const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
                filename = `banniere/${Date.now()}.${ext}`;
            }
            const blob = await upload(filename, body, {
                access: 'public',
                handleUploadUrl: '/api/upload-token',
            });
            const type = isVideo ? 'video' : 'image';
            const res = await fetch('/api/banniere', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ type, url: blob.url }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erreur');
            setBanniere({ type, url: blob.url });
            setBanniereStatus('Bandeau mis à jour !');
        } catch (error) {
            setBanniereStatus(error.message || 'Erreur lors de l\'envoi.');
        } finally {
            setIsBanniereUploading(false);
        }
    };

    // --- Carrousel ---
    // Toutes les actions ci-dessous ne modifient que l'état local (`photos`).
    // Rien n'est envoyé au serveur avant un clic sur "Enregistrer" (saveCarousel) :
    // le carrousel affiché sur le site ne change donc qu'à ce moment-là.
    const handleAddPhoto = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        setIsUploading(true);
        setCarouselStatus('Traitement de la photo…');
        try {
            const resized = await resizeImageToWebp(file);
            const previewUrl = URL.createObjectURL(resized);
            setPhotos((prev) => [...prev, { key: `new-${Date.now()}-${Math.random()}`, id: null, url: previewUrl, file: resized }]);
            setIsCarouselDirty(true);
            setCarouselStatus('Photo ajoutée (pense à Enregistrer).');
        } catch (error) {
            setCarouselStatus(error.message || "Erreur lors de l'ajout.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleReplacePhoto = async (key, e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        setIsUploading(true);
        setCarouselStatus('Traitement de la photo…');
        try {
            const resized = await resizeImageToWebp(file);
            const previewUrl = URL.createObjectURL(resized);
            setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, url: previewUrl, file: resized } : p)));
            setIsCarouselDirty(true);
            setCarouselStatus('Photo remplacée (pense à Enregistrer).');
        } catch (error) {
            setCarouselStatus(error.message || 'Erreur lors du remplacement.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeletePhoto = (key) => {
        setPhotos((prev) => prev.filter((p) => p.key !== key));
        setIsCarouselDirty(true);
        setCarouselStatus('Photo supprimée (pense à Enregistrer).');
    };

    const movePhoto = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= photos.length) return;
        setPhotos((prev) => {
            const reordered = [...prev];
            const [moved] = reordered.splice(index, 1);
            reordered.splice(newIndex, 0, moved);
            return reordered;
        });
        setIsCarouselDirty(true);
    };

    const saveCarousel = async () => {
        setIsSavingCarousel(true);
        setCarouselStatus('Enregistrement…');
        try {
            // Photos présentes avant et disparues du tableau local -> à supprimer.
            const currentIds = new Set(photos.filter((p) => p.id).map((p) => p.id));
            const toDelete = originalCarouselRef.current.filter((p) => !currentIds.has(p.id));
            for (const photo of toDelete) {
                const res = await fetch(`/api/carousel?id=${photo.id}`, { method: 'DELETE', credentials: 'include' });
                if (!res.ok) throw new Error('Suppression impossible.');
            }

            // Ajouts et remplacements : upload Blob puis écriture en base.
            const finalPhotos = [];
            for (const photo of photos) {
                if (photo.file) {
                    const filename = `carousel/${Date.now()}-${Math.random().toString(36).slice(2)}.webp`;
                    const blob = await upload(filename, photo.file, {
                        access: 'public',
                        handleUploadUrl: '/api/upload-token',
                    });
                    if (photo.id) {
                        const res = await fetch(`/api/carousel?id=${photo.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ url: blob.url }),
                        });
                        if (!res.ok) throw new Error('Remplacement impossible.');
                        finalPhotos.push({ id: photo.id });
                    } else {
                        const res = await fetch('/api/carousel', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ url: blob.url }),
                        });
                        if (!res.ok) throw new Error('Ajout impossible.');
                        const data = await res.json();
                        finalPhotos.push({ id: data.photo.id });
                    }
                } else {
                    finalPhotos.push({ id: photo.id });
                }
            }

            // Ordre final (uniquement s'il reste au moins une photo).
            if (finalPhotos.length > 0) {
                const orderRes = await fetch('/api/carousel', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ order: finalPhotos.map((p) => p.id) }),
                });
                if (!orderRes.ok) throw new Error('Réorganisation impossible.');
            }

            setCarouselStatus('Carrousel enregistré !');

            const listRes = await fetch('/api/carousel');
            const listData = await listRes.json();
            const loaded = (listData.photos || []).map((p) => ({ key: `existing-${p.id}`, id: p.id, url: p.url, file: null }));
            setPhotos(loaded);
            originalCarouselRef.current = loaded.map((p) => ({ id: p.id, url: p.url }));
            setIsCarouselDirty(false);
        } catch (error) {
            setCarouselStatus(error.message || "Erreur lors de l'enregistrement.");
        } finally {
            setIsSavingCarousel(false);
        }
    };

    const handleLogout = async () => {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
        navigate('/');
    };

    const hasModaleContent = (modale.titre && modale.titre.trim() !== '') || (modale.message && modale.message.trim() !== '');

    return (
        <div className="adminPanel">
            <div className="adminPanel__header">
                <h1>Panneau d'administration</h1>
                <button type="button" className="adminPanel__logout" onClick={handleLogout}>
                    Déconnexion
                </button>
            </div>

            <section className="adminPanel__section">
                <h2>Annonce (popup cloche)</h2>
                <form onSubmit={saveModale}>
                    <label className="adminPanel__checkbox">
                        <input
                            type="checkbox"
                            checked={modale.active}
                            onChange={(e) => setModale({ ...modale, active: e.target.checked })}
                        />
                        Annonce active
                    </label>
                    <input
                        type="text"
                        placeholder="Titre"
                        value={modale.titre}
                        onChange={(e) => setModale({ ...modale, titre: e.target.value })}
                    />
                    <textarea
                        rows={4}
                        placeholder="Message (texte, ou lien YouTube)"
                        value={modale.message}
                        onChange={(e) => setModale({ ...modale, message: e.target.value })}
                    />
                    <div className="adminPanel__preview adminPanel__preview--modale">
                        <span className="adminPanel__preview-label">Aperçu {!modale.active && '(annonce désactivée)'}</span>
                        <div className="adminPanel__preview-modale-card">
                            <div className="adminPanel__preview-modale-topbar"></div>
                            <div className="adminPanel__preview-modale-body">
                                {hasModaleContent ? (
                                    <>
                                        {modale.titre && <h3>{modale.titre}</h3>}
                                        {modale.message && <p>{modale.message}</p>}
                                    </>
                                ) : (
                                    <p className="adminPanel__preview-empty">Pas de nouvelle, bonne nouvelle !</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="adminPanel__actions">
                        <button type="submit">Enregistrer</button>
                        {modaleStatus && <span className="adminPanel__status">{modaleStatus}</span>}
                    </div>
                </form>
            </section>

            <section className="adminPanel__section">
                <h2>Bandeau d'accueil (vidéo ou photo)</h2>
                <div className="adminPanel__preview">
                    <span className="adminPanel__preview-label">Aperçu</span>
                    <div className="adminPanel__banniere-preview">
                        {banniere.type === 'image' ? (
                            <img src={banniere.url} alt="Bandeau actuel" />
                        ) : (
                            <video src={banniere.url} muted loop playsInline controls />
                        )}
                    </div>
                </div>
                <div className="adminPanel__actions adminPanel__actions--top">
                    <label className="adminPanel__upload-btn">
                        {isBanniereUploading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Changer la vidéo / photo'}
                        <input
                            type="file"
                            accept="video/*,image/*"
                            onChange={handleBanniereUpload}
                            disabled={isBanniereUploading}
                            hidden
                        />
                    </label>
                    {banniereStatus && <span className="adminPanel__status">{banniereStatus}</span>}
                </div>
            </section>

            <section className="adminPanel__section">
                <h2>Horaires</h2>
                <p className="adminPanel__help">
                    Sélectionne du texte et clique sur "<strong>B</strong>" pour le mettre en gras, ou "<em>I</em>" pour l'italique.
                    <br />
                    Appuie sur Entrée pour revenir à la ligne.
                </p>
                <form onSubmit={saveHoraire}>
                    <div className="adminPanel__toolbar">
                        <button
                            type="button"
                            onClick={() => wrapSelection(horaireRef.current, horaire, setHoraire, '/*', '*/')}
                            aria-label="Gras"
                            title="Gras"
                        >
                            <FontAwesomeIcon icon={faBold} />
                        </button>
                        <button
                            type="button"
                            onClick={() => wrapSelection(horaireRef.current, horaire, setHoraire, '//', '//')}
                            aria-label="Italique"
                            title="Italique"
                        >
                            <FontAwesomeIcon icon={faItalic} />
                        </button>
                    </div>
                    <textarea
                        ref={horaireRef}
                        rows={3}
                        value={horaire}
                        onChange={(e) => setHoraire(e.target.value)}
                        placeholder="mercredi, vendredi 10h - 13h30 | 15h-19h"
                    />
                    <div className="adminPanel__preview">
                        <span className="adminPanel__preview-label">Aperçu</span>
                        <p dangerouslySetInnerHTML={{ __html: applyFormatting(horaire) || '<span class="adminPanel__preview-empty">—</span>' }} />
                    </div>
                    <div className="adminPanel__actions">
                        <button type="submit">Enregistrer</button>
                        {horaireStatus && <span className="adminPanel__status">{horaireStatus}</span>}
                    </div>
                </form>
            </section>

            <section className="adminPanel__section">
                <h2>Photos du carrousel (accueil)</h2>

                {photos.length > 0 && (
                    <div className="adminPanel__preview">
                        <span className="adminPanel__preview-label">Aperçu</span>
                        <MyCarousel pictures={photos.map((p) => p.url)} />
                    </div>
                )}

                <div className="adminPanel__carousel-list">
                    {photos.map((photo, index) => (
                        <div className="adminPanel__carousel-row" key={photo.key}>
                            <span className="adminPanel__carousel-index">{index + 1}</span>
                            <img src={photo.url} alt="" className="adminPanel__carousel-thumb" />

                            <div className="adminPanel__carousel-row-actions">
                                <button
                                    type="button"
                                    onClick={() => movePhoto(index, -1)}
                                    disabled={index === 0}
                                    aria-label="Monter"
                                    title="Monter"
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => movePhoto(index, 1)}
                                    disabled={index === photos.length - 1}
                                    aria-label="Descendre"
                                    title="Descendre"
                                >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                </button>
                                <label className="adminPanel__icon-btn" title="Remplacer">
                                    <FontAwesomeIcon icon={faPen} />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => handleReplacePhoto(photo.key, e)}
                                        disabled={isUploading || isSavingCarousel}
                                    />
                                </label>
                                <button
                                    type="button"
                                    className="adminPanel__icon-btn adminPanel__icon-btn--danger"
                                    onClick={() => handleDeletePhoto(photo.key)}
                                    disabled={isSavingCarousel}
                                    aria-label="Supprimer"
                                    title="Supprimer"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="adminPanel__actions">
                    <label className="adminPanel__upload-btn">
                        {isUploading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Ajouter une photo'}
                        <input type="file" accept="image/*" onChange={handleAddPhoto} disabled={isUploading || isSavingCarousel} hidden />
                    </label>
                    <button type="button" onClick={saveCarousel} disabled={isSavingCarousel || !isCarouselDirty}>
                        {isSavingCarousel ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Enregistrer'}
                    </button>
                    {carouselStatus && <span className="adminPanel__status">{carouselStatus}</span>}
                </div>
            </section>

            <section className="adminPanel__section">
                <h2>Contact</h2>
                <form onSubmit={saveContact}>
                    <input
                        type="text"
                        placeholder="Téléphone"
                        value={contact.telephone}
                        onChange={(e) => setContact({ ...contact, telephone: e.target.value })}
                    />
                    <input
                        type="email"
                        placeholder="E-mail"
                        value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    />
                    <textarea
                        rows={2}
                        placeholder={'17 rue Anatole France\n42300 Roanne'}
                        value={contact.adresse}
                        onChange={(e) => setContact({ ...contact, adresse: e.target.value })}
                    />
                    <div className="adminPanel__preview">
                        <span className="adminPanel__preview-label">Aperçu</span>
                        <p>
                            {contact.telephone || '—'}<br />
                            {contact.email || '—'}<br />
                            {(contact.adresse || '—').split('\n').map((line, i) => (
                                <span key={i}>{line}<br /></span>
                            ))}
                        </p>
                    </div>
                    <div className="adminPanel__actions">
                        <button type="submit">Enregistrer</button>
                        {contactStatus && <span className="adminPanel__status">{contactStatus}</span>}
                    </div>
                </form>
            </section>
        </div>
    );
}

export default AdminPanel;
